<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { VServerInfo } from '@/types/dashboard/vserver/VServerInfo'
import { STATUS_CODES } from '@/constants'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { DashboardReasonConfirmDialog } from '@/components/dashboard/common'
import { formatTimestampSeconds } from '@/utils'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const server = ref<VServerInfo | null>(null)
const loading = ref(true)
const busy = ref(false)
const lockDialogOpen = ref(false)

const id = computed(() => Number(route.params.id))

async function fetchServer() {
  loading.value = true
  try {
    const res = await useAxios<null, VServerInfo>(
      'GET',
      `/admin/vserver/servers/${id.value}`,
      true
    )
    if (res?.status === STATUS_CODES.OK) {
      const parsed = VServerInfo.safeParse(res.data)
      if (parsed.success) server.value = parsed.data
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerDetail' })
  } finally {
    loading.value = false
  }
}

async function forceAction(action: 'provision' | 'deprovision') {
  if (!window.confirm(t(`dashboard.vserver.admin.detail.force${action === 'provision' ? 'Provision' : 'Deprovision'}`))) return
  busy.value = true
  try {
    await useAxios('POST', `/admin/vserver/servers/${id.value}/${action}`, true)
    await fetchServer()
  } catch (err) {
    handleAxiosError(err, { logPrefix: `AdminVServerDetail:${action}` })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.errors.actionFailed'),
      message: getErrorMessage(err)
    })
  } finally {
    busy.value = false
  }
}

async function lockAction(action: 'lock' | 'unlock', note?: string) {
  if (action === 'unlock' && !window.confirm(t('dashboard.vserver.admin.detail.unlockConfirm'))) return
  busy.value = true
  try {
    await useAxios<{ note: string }, unknown>(
      'POST',
      `/admin/vserver/servers/${id.value}/${action}`,
      true,
      note ? { note } : undefined
    )
    lockDialogOpen.value = false
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.vserver.admin.detail.title'),
      message: t(`dashboard.vserver.admin.detail.${action}Success`)
    })
    await fetchServer()
  } catch (err) {
    handleAxiosError(err, { logPrefix: `AdminVServerDetail:${action}` })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.errors.actionFailed'),
      message: getErrorMessage(err)
    })
    // A lock can be recorded and then fail to stop the VM, so reload rather
    // than keep showing the state from before.
    await fetchServer()
  } finally {
    busy.value = false
  }
}

onMounted(fetchServer)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.vserver.admin.detail.title')"
    :subtitle="server?.hostname ?? ''"
    icon="mdi-server"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminVServer' }"
  >
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <template v-else-if="server">
      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.vserver.overview.sectionTitle') }}</h3>
        </div>
        <div class="kv">
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.hostname') }}</span>
            <span class="mono">{{ server.hostname }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.status') }}</span>
            <span>{{ $t(`dashboard.vserver.status.${server.status}`) }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.ipv4') }}</span>
            <span class="mono">{{ server.ipv4_address ?? '-' }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.ipv6') }}</span>
            <span class="mono">{{ server.ipv6_address ?? '-' }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.overview.plan') }}</span>
            <span>{{ server.plan_slug }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.admin.detail.proxmoxVmid') }}</span>
            <span class="mono">{{ server.proxmox_vmid ?? '-' }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.vserver.admin.detail.proxmoxNode') }}</span>
            <span class="mono">{{ server.proxmox_node ?? '-' }}</span>
          </div>
          <div v-if="server.error_message" class="kv-row error">
            <span class="kv-label">{{ $t('dashboard.vserver.admin.detail.errorLabel') }}</span>
            <span>{{ server.error_message }}</span>
          </div>
        </div>
      </div>

      <div class="section-card lock-card" :class="{ 'is-locked': server.locked }">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.vserver.admin.detail.lockTitle') }}</h3>
        </div>
        <template v-if="server.locked">
          <p class="lock-state">
            {{ $t('dashboard.vserver.admin.detail.lockedSince', { date: formatTimestampSeconds(server.locked_at) }) }}
          </p>
          <blockquote v-if="server.lock_note" class="lock-note">{{ server.lock_note }}</blockquote>
          <button type="button" class="btn-text unlock-btn" :disabled="busy" @click="lockAction('unlock')">
            <v-icon icon="mdi-lock-open-variant-outline" size="18" />
            {{ $t('dashboard.vserver.admin.detail.unlockAction') }}
          </button>
        </template>
        <template v-else>
          <p class="lock-state">{{ $t('dashboard.vserver.admin.detail.lockDescription') }}</p>
          <button type="button" class="btn-text btn-danger lock-btn" :disabled="busy" @click="lockDialogOpen = true">
            <v-icon icon="mdi-lock-outline" size="18" />
            {{ $t('dashboard.vserver.admin.detail.lockAction') }}
          </button>
        </template>
      </div>

      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.vserver.admin.detail.title') }}</h3>
        </div>
        <div class="actions-row">
          <button
            type="button"
            class="btn-text"
            :disabled="busy"
            @click="forceAction('provision')"
          >
            <v-icon icon="mdi-cog-play" size="18" />
            {{ $t('dashboard.vserver.admin.detail.forceProvision') }}
          </button>
          <button
            type="button"
            class="btn-text btn-danger"
            :disabled="busy"
            @click="forceAction('deprovision')"
          >
            <v-icon icon="mdi-delete-sweep" size="18" />
            {{ $t('dashboard.vserver.admin.detail.forceDeprovision') }}
          </button>
          <button type="button" class="btn-text" @click="router.push({ name: 'dashboardAdminVServer' })">
            <v-icon icon="mdi-arrow-left" size="18" />
            {{ $t('dashboard.vserver.admin.detail.backToList') }}
          </button>
        </div>
      </div>
    </template>

    <DashboardReasonConfirmDialog
      v-model="lockDialogOpen"
      variant="danger"
      icon="mdi-lock-outline"
      :title="t('dashboard.vserver.admin.detail.lockDialogTitle')"
      :description="t('dashboard.vserver.admin.detail.lockDialogDescription', { hostname: server?.hostname ?? '' })"
      :reason-label="t('dashboard.vserver.admin.detail.lockNoteLabel')"
      :confirm-text="t('dashboard.vserver.admin.detail.lockAction')"
      :loading="busy"
      :min-length="10"
      :max-length="1000"
      @confirm="(note: string) => lockAction('lock', note)"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.kv {
  display: flex;
  flex-direction: column;
}

.kv-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &.error {
    color: #ef5350;
  }
}

.kv-label {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.85rem;
}

.mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.9);
}

.actions-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.85);
  font-size: 0.82rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
  }
}

.btn-danger {
  background: rgba(244, 67, 54, 0.08);
  border-color: rgba(244, 67, 54, 0.25);
  color: #ef5350;
}

.state-box {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.lock-card.is-locked {
  border-color: rgba(244, 67, 54, 0.3);
}

.lock-state {
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.lock-note {
  margin: 0 0 12px;
  padding: 10px 14px;
  border-left: 3px solid rgba(244, 67, 54, 0.4);
  border-radius: 0 8px 8px 0;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.85);
  white-space: pre-wrap;
}
</style>
